import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {Clock, ArrowRight} from 'lucide-react'

const FinalCTA = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section ref={ref} className="py-20 md:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-[#0D7FA3] relative overflow-hidden">
      {/* Animated clock background */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-5"
      >
        <Clock className="w-full h-full text-white" strokeWidth={0.5} />
      </motion.div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex items-center justify-center w-32 h-32 bg-white/10 backdrop-blur-sm rounded-full mb-8 border-4 border-white/20"
            >
              <Clock className="w-16 h-16 text-yellow-300" strokeWidth={2} />
            </motion.div>

            <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-6 leading-tight">
              O tempo vai passar
              <br />
              <span className="text-yellow-300">de qualquer forma</span>
            </h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-2xl md:text-3xl text-white/90 mb-12 leading-relaxed font-light"
          >
            A diferença é se, daqui a alguns meses, seu inglês vai estar{' '}
            <span className="line-through decoration-red-400 decoration-2">no mesmo lugar</span>{' '}
            — ou <span className="font-bold text-yellow-300">melhor</span>.
          </motion.p>

          {/* Visual comparison */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="grid md:grid-cols-3 gap-6 mb-12 items-center"
          >
            <div className="bg-red-500/20 border-2 border-red-500/50 rounded-2xl p-6 backdrop-blur-sm">
              <p className="text-lg text-white font-semibold mb-2">Daqui 6 meses</p>
              <p className="text-4xl font-black text-red-400 mb-2">😔</p>
              <p className="text-white/80">Ainda no mesmo lugar</p>
            </div>

            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="flex items-center justify-center"
            >
              <ArrowRight className="w-12 h-12 text-yellow-300" strokeWidth={3} />
            </motion.div>

            <div className="bg-green-500/20 border-2 border-green-500/50 rounded-2xl p-6 backdrop-blur-sm">
              <p className="text-lg text-white font-semibold mb-2">Daqui 6 meses</p>
              <p className="text-4xl font-black text-green-400 mb-2">🎉</p>
              <p className="text-white/80">Evoluindo com estrutura</p>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <motion.button
              onClick={scrollToPricing}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="relative bg-gradient-to-r from-yellow-300 to-yellow-400 hover:from-yellow-400 hover:to-yellow-500 text-gray-900 font-bold text-xl md:text-2xl px-12 md:px-16 py-6 rounded-full shadow-2xl transition-all duration-300 overflow-hidden group"
            >
              <span className="relative z-10 flex items-center gap-3">
                Começar agora no EW Pass
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            </motion.button>

            <motion.p
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="text-white/70 mt-6 text-lg"
            >
              ✨ Comece hoje • Garantia de 6 meses • Cancele quando quiser
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default FinalCTA
